import React from "react";
import { TabsContent } from "../ui/tabs";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { RefreshCw, Key, Eye, EyeOff, Send, Rss } from "lucide-react";

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

export default function EmailTab({
  dm,
  brevoStats, loadingBrevo, syncingAll, loadBrevoStats, syncAllToBrevo,
  resendKey, setResendKey, showResendKey, setShowResendKey,
  fromEmail, setFromEmail, fromName, setFromName,
  siteUrl, setSiteUrl, saving, saveEmailConfig, testEmail,
}) {
  return (
    <TabsContent value="email">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Brevo Stats */}
        <div className={`rounded-2xl p-7 ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" }}>
          <div className="flex items-center justify-between mb-1">
            <h3 className={`editorial-heading text-xl font-medium ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Brevo</h3>
            <div className="flex items-center gap-2">
              <button onClick={syncAllToBrevo} disabled={syncingAll} className={`category-tag flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all disabled:opacity-50 ${dm ? "bg-cyan-400/10 text-cyan-400 hover:bg-cyan-400/20" : "bg-[#006874]/8 text-[#006874] hover:bg-[#006874]/15"}`}>
                <RefreshCw className={`w-3 h-3 ${syncingAll ? "animate-spin" : ""}`} />{syncingAll ? "Syncing…" : "Sync all users"}
              </button>
              <button onClick={loadBrevoStats} className={`category-tag flex items-center gap-1.5 px-3 py-1.5 rounded-full ${dm ? "bg-[#252e2f] text-slate-400 hover:bg-[#303b3c]" : "bg-[#f3f4f5] hover:bg-[#e7e8e9] text-[#6b7a7d]"}`}>
                <RefreshCw className={`w-3 h-3 ${loadingBrevo ? "animate-spin" : ""}`} />Refresh stats
              </button>
            </div>
          </div>
          <p className={`category-tag mb-6 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>PRIMARY EMAIL PROVIDER · CONTACTS & CAMPAIGNS</p>

          {!brevoStats ? (
            <div className="space-y-4">
              <div className={`rounded-xl p-4 ${dm ? "bg-[#252e2f]" : "bg-[#f3f4f5]"}`}>
                <p className={`text-sm font-medium mb-1 ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>Setup required</p>
                <p className={`category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>Add your <strong>BREVO_API_KEY</strong> in Admin → API Keys, then click Sync to connect.</p>
              </div>
              <div className="space-y-2">
                {["Sends magic link emails (primary, Resend as fallback)", "Auto-syncs subscribers to 'TourismIntel Subscribers' list", "One-click campaign send from Newsletters tab", "Track opens, clicks and delivery rates"].map(f => (
                  <div key={f} className="flex items-start gap-2">
                    <Rss className={`w-3 h-3 mt-0.5 flex-shrink-0 ${dm ? "text-cyan-400/50" : "text-[#006874]/50"}`} />
                    <span className={`category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>{f}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : !brevoStats.configured ? (
            <div className={`rounded-xl p-4 ${dm ? "bg-amber-400/8" : "bg-amber-50"}`}>
              <p className={`text-sm ${dm ? "text-amber-400" : "text-amber-700"}`}>{brevoStats.message}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {brevoStats.webhook_url && (
                <div className={`rounded-xl p-4 ${dm ? "bg-[#252e2f]" : "bg-[#f3f4f5]"}`}>
                  <p className={`category-tag mb-1 ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>Unsubscribe Webhook URL</p>
                  <p className={`category-tag mb-2 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>Add this in Brevo → Transactional → Settings → Webhooks to automatically revoke access when users unsubscribe.</p>
                  <div className="flex items-center gap-2">
                    <code className={`flex-1 text-xs px-3 py-2 rounded-lg font-mono truncate ${dm ? "bg-[#1a2122] text-cyan-400" : "bg-white text-[#006874]"}`}>{brevoStats.webhook_url}</code>
                    <button onClick={() => { navigator.clipboard.writeText(brevoStats.webhook_url); }} className={`category-tag px-3 py-2 rounded-lg flex-shrink-0 ${dm ? "bg-[#1a2122] text-slate-400 hover:text-slate-200" : "bg-white text-[#6b7a7d] hover:text-[#191c1d]"}`}>Copy</button>
                  </div>
                </div>
              )}
              <div className={`rounded-xl p-4 flex items-center justify-between ${dm ? "bg-[#252e2f]" : "bg-[#f3f4f5]"}`}>
                <div>
                  <p className={`category-tag mb-0.5 ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>Subscriber list</p>
                  <p className={`text-sm font-semibold ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>{brevoStats.list_name}</p>
                </div>
                <div className="text-right">
                  <p className={`editorial-heading text-2xl font-medium ${dm ? "text-cyan-400" : "text-[#006874]"}`}>{brevoStats.total_subscribers}</p>
                  <p className={`category-tag ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>contacts</p>
                </div>
              </div>
              {brevoStats.campaigns?.length > 0 && (
                <div>
                  <p className={`category-tag mb-2 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>RECENT CAMPAIGNS</p>
                  <div className="space-y-2">
                    {brevoStats.campaigns.map(c => (
                      <div key={c.id} className={`rounded-xl p-3 ${dm ? "bg-[#252e2f]" : "bg-[#f3f4f5]"}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className={`text-xs font-medium truncate ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>{c.subject}</p>
                            <p className={`category-tag ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>{c.sent_at ? new Date(c.sent_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "Draft"}</p>
                          </div>
                          <span className={`px-2 py-0.5 rounded-full category-tag flex-shrink-0 ${c.status === "sent" ? (dm ? "bg-emerald-400/10 text-emerald-400" : "bg-emerald-50 text-emerald-700") : (dm ? "bg-[#303b3c] text-slate-400" : "bg-white text-[#6b7a7d]")}`}>{c.status}</span>
                        </div>
                        {c.stats && (
                          <div className="flex gap-4 mt-2">
                            {[["Delivered", c.stats.delivered], ["Opened", c.stats.opened], ["Clicked", c.stats.clicks]].map(([l, v]) => (
                              <div key={l}>
                                <p className={`text-xs font-medium ${dm ? "text-slate-300" : "text-[#191c1d]"}`}>{v}</p>
                                <p className={`category-tag ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>{l}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Resend config */}
        <div className={`rounded-2xl p-7 ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" }}>
          <h3 className={`editorial-heading text-xl font-medium mb-1 ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Resend Configuration</h3>
          <p className={`category-tag mb-6 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>TRANSACTIONAL EMAIL FOR MAGIC LINKS & CONFIRMATIONS</p>
          <div className="space-y-5">
            <div>
              <Label className={`category-tag mb-1.5 block ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
                <span className="flex items-center gap-1.5"><Key className="w-3 h-3" />Resend API Key</span>
              </Label>
              <div className="flex gap-2">
                <Input type={showResendKey ? "text" : "password"} value={resendKey}
                  onChange={(e) => setResendKey(e.target.value)}
                  placeholder="re_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  className={`border-0 font-mono text-sm ${dm ? "bg-[#252e2f] text-slate-200 placeholder:text-slate-600" : "bg-[#f3f4f5]"}`} />
                <button onClick={() => setShowResendKey(!showResendKey)}
                  className={`p-2.5 rounded-lg transition-colors ${dm ? "bg-[#252e2f] text-slate-400 hover:bg-[#303b3c]" : "bg-[#f3f4f5] hover:bg-[#e7e8e9]"}`}>
                  {showResendKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className={`category-tag mt-1.5 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>Get your key at resend.com → API Keys</p>
            </div>
            <div>
              <Label className={`category-tag mb-1.5 block ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>From Email Address</Label>
              <Input value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} placeholder="noreply@yourdomain.com" className={`border-0 ${dm ? "bg-[#252e2f] text-slate-200" : "bg-[#f3f4f5]"}`} />
              <p className={`category-tag mt-1.5 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>Must be verified in Resend dashboard</p>
            </div>
            <div>
              <Label className={`category-tag mb-1.5 block ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>From Name</Label>
              <Input value={fromName} onChange={(e) => setFromName(e.target.value)} className={`border-0 ${dm ? "bg-[#252e2f] text-slate-200" : "bg-[#f3f4f5]"}`} />
            </div>
            <div>
              <Label className={`category-tag mb-1.5 block ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>Site URL</Label>
              <Input value={siteUrl} onChange={(e) => setSiteUrl(e.target.value)} placeholder="https://your-site.com" className={`border-0 ${dm ? "bg-[#252e2f] text-slate-200" : "bg-[#f3f4f5]"}`} />
              <p className={`category-tag mt-1.5 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>Used for magic link generation in emails</p>
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={saveEmailConfig} className={`flex-1 py-2.5 rounded-full category-tag transition-all ${dm ? "bg-cyan-400 text-[#001f24] hover:bg-cyan-300" : "bg-[#006874] text-white hover:bg-[#004f58]"}`}>
                {saving ? <span className="flex items-center justify-center gap-2"><RefreshCw className="w-3.5 h-3.5 animate-spin" />Saving…</span> : "Save Configuration"}
              </button>
              <button onClick={testEmail} className={`px-5 py-2.5 rounded-full category-tag transition-all flex items-center gap-2 ${dm ? "bg-[#252e2f] text-slate-300 hover:bg-[#303b3c]" : "bg-[#f3f4f5] text-[#3b494c] hover:bg-[#e7e8e9]"}`}>
                <Send className="w-3.5 h-3.5" />Test
              </button>
            </div>
          </div>
        </div>

        {/* Email Preview */}
        <div className={`rounded-2xl p-7 ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" }}>
          <h3 className={`editorial-heading text-xl font-medium mb-1 ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Email Preview</h3>
          <p className={`category-tag mb-6 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>MAGIC LINK TEMPLATE SENT TO USERS</p>
          <div className={`rounded-xl overflow-hidden border ${dm ? "border-white/8" : "border-black/6"}`}>
            <div className={`px-5 py-3 ${dm ? "bg-[#0c1011]" : "bg-[#f8f9fa]"} border-b ${dm ? "border-white/8" : "border-black/6"}`}>
              <p className={`category-tag ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>From: {fromName} &lt;{fromEmail}&gt;</p>
              <p className={`category-tag ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>Subject: Your login link for TourismIntel</p>
            </div>
            <div className="p-6 bg-white">
              <div className="max-w-sm mx-auto text-center">
                <div style={{ background: "#006874", borderRadius: 12, padding: "20px 24px", marginBottom: 20 }}>
                  <img src="/TI_logo_white_crop.png" alt="tourismintel.ai" style={{ display: "block", margin: "0 auto", height: 36, width: 160, objectFit: "contain", maxWidth: "none" }} />
                  <p style={{ fontFamily: "sans-serif", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: "rgba(255,255,255,0.6)", margin: "8px 0 0" }}>Intelligence for Tourism Professionals</p>
                </div>
                <p style={{ fontFamily: "sans-serif", fontSize: 13, color: "#6b7a7d", marginBottom: 24 }}>Click the button below to log in securely. This link expires in 15 minutes.</p>
                <div style={{ background: "#006874", color: "#fff", padding: "12px 28px", borderRadius: 32, display: "inline-block", fontFamily: "sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 24 }}>
                  Log In to tourismintel.ai →
                </div>
                <p style={{ fontFamily: "sans-serif", fontSize: 11, color: "#bac9cc" }}>If you didn&apos;t request this, you can safely ignore this email.</p>
                <p style={{ fontFamily: "sans-serif", fontSize: 10, color: "#d9dadb", marginTop: 16 }}>© 2026 tourismintel.ai · Unsubscribe</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </TabsContent>
  );
}
